"use client"

/**
 * Composant principal pour créer et gérer les sessions partagées VisualGrid
 * FONCTIONNALITÉ RÉSERVÉE AUX UTILISATEURS PRO
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import useTauri from '@/hooks/useTauri';
import { useAuth } from '@/lib/auth-provider';
import { useLanguage } from '@/lib/i18n/language-provider';
import { SupabaseSessionManager } from '@/lib/sessions/supabase-session-manager';
import { SessionManager } from '@/lib/sessions/session-manager';
import { SessionInfo } from '@/lib/sessions/types';
import {
  Cloud,
  Share2,
  CheckCircle,
  Copy,
  Monitor,
  Loader2,
  Users,
  Smartphone,
  LogOut,
  Power,
  Crown,
  Lock
} from 'lucide-react';
import Link from 'next/link';

interface SessionCreatorProps {
  onSessionCreated?: (sessionInfo: SessionInfo, manager: SessionManager) => void;
  onSessionJoined?: (sessionInfo: SessionInfo, manager: SessionManager) => void;
  className?: string;
  hideCreate?: boolean; // Masquer le bouton "Créer une session"
  hideJoin?: boolean;   // Masquer le bouton "Rejoindre une session"
  autoJoinCode?: string | null; // Code pour rejoindre automatiquement depuis URL
}

export default function SessionCreator({
  onSessionCreated,
  onSessionJoined,
  className,
  hideCreate = false,
  hideJoin = false,
  autoJoinCode = null
}: SessionCreatorProps) {
  // Vérification du statut PRO
  const { isPro } = useAuth();
  const { t } = useLanguage();
  // useTauri supprimé - sessions locales désactivées

  const [mode, setMode] = useState<'create' | 'join' | 'active'>('create');
  // Forcer le mode cloud uniquement - les sessions locales sont désactivées
  const sessionMode: 'cloud' = 'cloud';
  const [activeSession, setActiveSession] = useState<SessionInfo | null>(null);
  const [sessionManager, setSessionManager] = useState<SessionManager | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [connectedDevices, setConnectedDevices] = useState<any[]>([]);
  const [isDeviceConnected, setIsDeviceConnected] = useState(false);
  const [isDetectingDevice, setIsDetectingDevice] = useState(true);
  const [deviceType, setDeviceType] = useState<'mobile' | 'desktop' | null>(null);
  const [showProModal, setShowProModal] = useState(false);

  // QR code ref (gardé pour compatibilité si nécessaire)
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  // Vérifier si Supabase est configuré
  const supabaseConfigured = !!(typeof window !== 'undefined' && process.env.NEXT_PUBLIC_SUPABASE_URL);

  // Détection du type d'appareil avec délai pour une meilleure UX
  useEffect(() => {
    const detectDevice = async () => {
      // Attendre que useIsMobile soit stable
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Détecter le type d'appareil de façon synchrone
      const userAgent = navigator.userAgent;
      const isMobileDevice = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Petit délai pour rendre l'animation de chargement visible
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setDeviceType(isMobileDevice ? 'mobile' : 'desktop');
      setIsDetectingDevice(false);
    };
    
    detectDevice();
  }, []); // Enlever la dépendance à isMobile pour éviter les re-détections

  // Nettoyer les ressources à la fermeture
  useEffect(() => {
    return () => {
      if (sessionManager) {
        sessionManager.disconnect();
      }
    };
  }, [sessionManager]);

  // Auto-join depuis URL
  useEffect(() => {
    if (autoJoinCode && !activeSession && !isLoading) {
      console.log(`🔗 [SESSION_CREATOR] Auto-join avec code: ${autoJoinCode}`);
      setJoinCode(autoJoinCode);
      setMode('join');
      // Trigger join après un court délai pour laisser l'UI se préparer
      setTimeout(() => {
        joinSession();
      }, 500);
    }
  }, [autoJoinCode]); // Ne pas inclure activeSession/isLoading pour éviter les loops

  // Sessions locales supprimées - fonctionnalité désactivée

  // QR code génération supprimée - sessions locales désactivées

  const createSession = async () => {
    // ⚠️ SÉCURITÉ: Vérification PRO (double vérification)
    if (supabaseConfigured && !isPro) {
      setShowProModal(true);
      return;
    }

    setIsLoading(true);

    try {
      // Créer le gestionnaire Supabase Realtime (remplace Socket.io)
      console.log('🔌 [SESSION_CREATOR] Création session avec Supabase Realtime...');
      const manager = new SupabaseSessionManager();

      // Configurer les callbacks
      manager.setCallbacks({
        onDeviceJoin: (device) => {
          setConnectedDevices(prev => [...prev, device]);
          setIsDeviceConnected(true);
          toast.success(t("toast.deviceConnectedRemote", { name: device.name }));
        },
        onDeviceLeave: (deviceId) => {
          setConnectedDevices(prev => prev.filter(d => d.id !== deviceId));
          if (connectedDevices.length <= 1) {
            setIsDeviceConnected(false);
          }
          toast.info(t('toast.remoteDisconnected'));
        },
        onSessionTerminated: () => {
          handleSessionEnd();
          toast.error(t('toast.sessionEnded'));
        },
        onConnectionLost: () => {
          toast.error(t('toast.connectionLost'));
        },
        onError: (error) => {
          toast.error(error);
        }
      });

      // Créer la session
      const sessionInfo = await manager.createSession({
        type: 'cloud',
        deviceInfo: {
          name: getDeviceName(),
          type: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop'
        }
      });

      setActiveSession(sessionInfo);
      setSessionManager(manager);
      setMode('active');

      // Initialiser l'état des appareils connectés basé sur la session
      if (sessionInfo.devices && sessionInfo.devices.length > 1) {
        const otherDevices = sessionInfo.devices.filter(d => d.id !== sessionInfo.deviceId);
        setConnectedDevices(otherDevices);
        setIsDeviceConnected(otherDevices.length > 0);
      }

      // Notifier le parent
      onSessionCreated?.(sessionInfo, manager);

      toast.success(t("toast.sessionCreated", { code: sessionInfo.code }));

    } catch (error: any) {
      // console.error('❌ [SESSION_CREATOR] Erreur création:', error);
      toast.error(error.message || t('toast.sessionCreationError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction createLocalSession supprimée - sessions locales désactivées

  const joinSession = async () => {
    // Les utilisateurs non-PRO peuvent rejoindre des sessions (pas les créer)
    if (!joinCode.trim()) {
      toast.error(t('toast.enterSessionCode'));
      return;
    }

    setIsLoading(true);

    try {
      // Rejoindre la session via Supabase Realtime (remplace Socket.io)
      console.log('🔌 [SESSION_CREATOR] Connexion session avec Supabase Realtime...');
      const manager = new SupabaseSessionManager();
      const sessionInfo = await manager.joinSession({
        sessionCode: joinCode.trim(),
        deviceInfo: {
          name: getDeviceName(),
          type: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop'
        }
      });

      // Configurer les callbacks
      manager.setCallbacks({
        onConfigUpdate: (config) => {
          // Transmettre au parent pour synchroniser l'interface
          console.log('📥 [SESSION_CREATOR] Config reçue:', config);
        },
        onDeviceJoin: (device) => {
          setConnectedDevices(prev => [...prev, device]);
          setIsDeviceConnected(true);
          toast.success(t("toast.deviceConnected", { name: device.name }));
        },
        onDeviceLeave: (deviceId) => {
          setConnectedDevices(prev => prev.filter(d => d.id !== deviceId));
          if (connectedDevices.length <= 1) {
            setIsDeviceConnected(false);
          }
          toast.info(t('toast.deviceDisconnected'));
        },
        onSessionTerminated: () => {
          handleSessionEnd();
          toast.error(t('toast.sessionEndedByServer'));
        },
        onConnectionLost: () => {
          toast.error(t('toast.connectionLost'));
        },
        onError: (error) => {
          toast.error(error);
        }
      });

      setActiveSession(sessionInfo);
      setSessionManager(manager);
      setMode('active');

      // Initialiser l'état des appareils connectés basé sur la session
      if (sessionInfo.devices && sessionInfo.devices.length > 1) {
        const otherDevices = sessionInfo.devices.filter(d => d.id !== sessionInfo.deviceId);
        setConnectedDevices(otherDevices);
        setIsDeviceConnected(otherDevices.length > 0);
      }

      // Notifier le parent
      onSessionJoined?.(sessionInfo, manager);

      toast.success(t("toast.connectedToSession", { code: sessionInfo.code }));

    } catch (error: any) {
      // console.error('❌ [SESSION_CREATOR] Erreur connexion:', error);
      toast.error(error.message || 'Impossible de rejoindre la session');
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction joinLocalSession supprimée - sessions locales désactivées

  const handleSessionEnd = async () => {
    if (sessionManager) {
      await sessionManager.disconnect();
    }
    
    setActiveSession(null);
    setSessionManager(null);
    setConnectedDevices([]);
    setIsDeviceConnected(false);
    setMode('create');
    setJoinCode('');
  };

  const copySessionCode = () => {
    if (activeSession?.code) {
      navigator.clipboard.writeText(activeSession.code);
      toast.success(t('toast.codeCopied'));
    }
  };

  const shareSession = () => {
    if (activeSession?.joinUrl) {
      if (navigator.share) {
        navigator.share({
          title: 'Session VisualGrid',
          text: `Rejoignez ma session VisualGrid avec le code: ${activeSession.code}`,
          url: activeSession.joinUrl
        });
      } else {
        navigator.clipboard.writeText(activeSession.joinUrl);
        toast.success(t('toast.sessionLinkCopied'));
      }
    }
  };

  const getDeviceName = (): string => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('Mac')) return 'Mac';
    if (userAgent.includes('Windows')) return 'PC Windows';
    if (userAgent.includes('Linux')) return 'Linux';
    return 'Appareil';
  };

  return (
    <div className={className}>
      
      {/* Session Active - Interface détaillée */}
      {mode === 'active' && activeSession && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                <Cloud className="w-5 h-5" />
                Session Active
              </div>
              {/* Voyant de connexion Simple */}
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${
                  isDeviceConnected 
                    ? 'bg-green-500 animate-pulse' 
                    : 'bg-gray-400'
                }`}></div>
                <span className="text-sm">
                  {isDeviceConnected ? `${connectedDevices.length} remote${connectedDevices.length > 1 ? 's' : ''}` : 'En attente'}
                </span>
              </div>
            </CardTitle>
            <CardDescription className="text-green-700 dark:text-green-300">
              <span className="flex items-center gap-1">
                <Cloud className="w-4 h-4" />
                Session Cloud - Accessible partout
              </span>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Code de session - Cloud uniquement */}
            <div className="text-center">
              <Label className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3 block">
                Code de Session
              </Label>

              <div className="bg-white dark:bg-gray-800 border-2 border-green-400 rounded-lg p-4 mb-3">
                <div className="text-3xl font-mono font-bold text-green-700 dark:text-green-300 tracking-wider">
                  {activeSession.code}
                </div>
              </div>

              <Button
                variant="outline"
                onClick={copySessionCode}
                className="w-full border-green-400 hover:bg-green-100 dark:border-green-600 dark:hover:bg-green-900"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copier le Code
              </Button>
            </div>

            {/* Appareils connectés */}
            <div>
              <Label className="text-sm font-medium text-green-700 dark:text-green-300 flex items-center gap-2 mb-3">
                <Users className="w-4 h-4" />
                Appareils connectés ({connectedDevices.length + 1})
              </Label>
              <div className="space-y-2">
                {/* Appareil actuel */}
                <div className="flex items-center gap-3 p-3 bg-white dark:bg-green-900 rounded-lg border border-green-200 dark:border-green-700">
                  <Monitor className="w-5 h-5 text-green-600" />
                  <span className="font-medium">{getDeviceName()} (vous)</span>
                  <Badge variant="outline" className="ml-auto border-green-400 text-green-700 dark:border-green-600 dark:text-green-300">
                    {activeSession.role === 'server' ? '🖥️ Server' : '📱 Remote'}
                  </Badge>
                </div>
                
                {/* Autres appareils */}
                {connectedDevices.map((device) => (
                  <div key={device.id} className="flex items-center gap-3 p-3 bg-white dark:bg-green-900 rounded-lg border border-green-200 dark:border-green-700">
                    <Smartphone className="w-5 h-5 text-blue-600" />
                    <span>{device.name}</span>
                    <Badge variant="outline" className="ml-auto border-blue-400 text-blue-700 dark:border-blue-600 dark:text-blue-300">
                      {device.role === 'server' ? '🖥️ Server' : '📱 Remote'}
                    </Badge>
                  </div>
                ))}
                
                {connectedDevices.length === 0 && (
                  <div className="text-center p-6 text-green-600 dark:text-green-400 bg-white dark:bg-green-900 rounded-lg border-2 border-dashed border-green-300 dark:border-green-700">
                    <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="font-medium">En attente de connexion remote...</p>
                    <p className="text-sm opacity-75">Partagez le code pour connecter un appareil</p>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions pour sessions locales supprimées - uniquement Cloud disponible */}

            {/* Actions - Boutons améliorés */}
            <div className="space-y-3">
              {/* Bouton Partager - Cloud uniquement */}
              <Button
                variant="outline"
                onClick={shareSession}
                className="w-full h-12 text-md font-semibold border-green-300 hover:bg-green-100 dark:border-green-700 dark:hover:bg-green-900 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950 dark:to-blue-950"
              >
                <Share2 className="w-5 h-5 mr-2" />
                📤 Partager le Code de Session
              </Button>

              {/* Bouton Déconnexion/Termination - Style différent selon le rôle et le mode */}
              <Button
                variant="destructive"
                onClick={handleSessionEnd}
                className={`w-full h-14 text-lg font-bold relative overflow-hidden ${
                  activeSession.role === 'server'
                    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/25'
                    : 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 shadow-lg shadow-orange-500/25'
                }`}
              >
                {activeSession.role === 'server' ? (
                  <>
                    <Power className="w-6 h-6 mr-3" />
                    Fermer la session
                  </>
                ) : (
                  <>
                    <LogOut className="w-6 h-6 mr-3" />
                    Quitter la session
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* État de chargement - Détection du type d'appareil */}
      {mode !== 'active' && isDetectingDevice && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-5 h-5 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin"></div>
              <span className="text-sm text-muted-foreground">Détection de l'appareil...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interface de sessions - Après détection */}
      {mode !== 'active' && !isDetectingDevice && (
        <>
          {/* Sélecteur Cloud/Local desktop supprimé - uniquement Cloud disponible */}

          {/* Créer une Session - Sur desktop OU quand hideJoin est actif (mobile en mode desktop) */}
          {((deviceType === 'desktop' || hideJoin) && !hideCreate) && (
            <Card className="mb-4 animate-in fade-in slide-in-from-bottom-4 duration-500 border-green-400/50 dark:border-green-600/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="w-5 h-5 text-green-600" />
                {t('sessions.createCloudSession')}
              </CardTitle>
              <CardDescription>
                {t('sessions.createSessionDesc')}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <Button
                onClick={createSession}
                disabled={isLoading}
                className="w-full h-16 relative overflow-hidden animate-[pulse_3s_ease-in-out_infinite] hover:animate-none transition-all bg-gradient-to-r from-green-500/10 to-purple-500/10 border-green-400 dark:border-green-600 shadow-[0_0_15px_rgba(130,59,246,0.5)] dark:shadow-[0_0_15px_rgba(197,147,253,0.5)] text-lg font-semibold text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950"
                variant="outline"
              >
                <div className="pointer-events-none absolute inset-y-0 -left-full w-full skew-x-12 bg-gradient-to-r from-transparent via-white/80 to-transparent dark:via-white/40 mix-blend-screen opacity-90 animate-wipe filter drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]"></div>
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin relative z-10" />
                    <span className="relative z-10">{t("sessions.creating")}</span>
                  </>
                ) : (
                  <>
                    <Cloud className="w-5 h-5 mr-2 relative z-10" />
                    <span className="relative z-10 font-bold">{t("sessions.createSession")}</span>
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
          )}

          {/* Sélecteur Cloud/Local supprimé - uniquement Cloud disponible */}

          {/* Rejoindre une Session - Affiché sur mobile OU si hideCreate est actif (mode remote) */}
          {((deviceType === 'mobile' || hideCreate) && !hideJoin) && (
            <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 border-green-400/50 dark:border-green-600/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  {t('sessions.controlSession')}
                </CardTitle>
                <CardDescription>
                  {t('sessions.controlSessionDesc')}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Session Code for Cloud Mode */}
                <div className="space-y-2">
                  <Label htmlFor="join-code">{t('sessions.sessionCode')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="join-code"
                      placeholder="Ex: ABC123"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      className="font-mono text-lg text-center tracking-widest"
                      maxLength={6}
                      disabled={isLoading}
                    />
                    <Button
                      onClick={joinSession}
                      disabled={isLoading || !joinCode.trim()}
                      className="px-6"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('sessions.join')
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal PRO pour la création de session */}
      <Dialog open={showProModal} onOpenChange={setShowProModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              {t('sessions.proFeature')}
            </DialogTitle>
            <DialogDescription>
              {t('sessions.proRequired')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-4 rounded-lg border-2 border-foreground/10 bg-muted/30">
              <Lock className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-semibold">
                  {t('sessions.proFeatureAllows')}
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>{t('sessions.proFeature1')}</li>
                  <li>{t('sessions.proFeature2')}</li>
                  <li>{t('sessions.proFeature3')}</li>
                </ul>
              </div>
            </div>

            <Link href="/pro" className="block">
              <Button className="w-full bg-foreground text-background hover:bg-foreground/90" size="lg">
                <Crown className="mr-2 h-5 w-5" />
                {t('sessions.upgradeToPro')}
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


